import { useEffect, useMemo, useState, type ReactNode } from 'react'
import { useForm, Controller } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Switch } from '@/components/ui/switch'
import { Select } from '@/components/ui/select'
import {
  noticiaFormSchema,
  type NoticiaFormValues,
} from '@/features/noticias/schema'
import { CtaUrlField } from './cta-url-field'
import { ImagePicker } from './image-picker'
import { MobilePreview } from './mobile-preview'

export interface NoticiaEditorSubmit {
  values: NoticiaFormValues
  imageFile: File | null
}

interface NoticiaEditorProps {
  title: string
  description?: string
  initialValues: NoticiaFormValues
  initialImageUrl?: string | null
  submitLabel: string
  submitting: boolean
  onSubmit: (payload: NoticiaEditorSubmit) => void | Promise<void>
  onCancel: () => void
}

export function NoticiaEditor({
  title,
  description,
  initialValues,
  initialImageUrl,
  submitLabel,
  submitting,
  onSubmit,
  onCancel,
}: NoticiaEditorProps): ReactNode {
  const {
    register,
    handleSubmit,
    control,
    watch,
    formState: { errors },
  } = useForm<NoticiaFormValues>({
    resolver: zodResolver(noticiaFormSchema),
    defaultValues: initialValues,
  })

  const [imageFile, setImageFile] = useState<File | null>(null)
  const [localPreview, setLocalPreview] = useState<string | null>(null)
  const [imageError, setImageError] = useState<string | null>(null)

  // Manage object URLs for the locally-selected file: revoke on replace/unmount.
  useEffect(() => {
    if (!imageFile) {
      setLocalPreview(null)
      return
    }
    const url = URL.createObjectURL(imageFile)
    setLocalPreview(url)
    return () => URL.revokeObjectURL(url)
  }, [imageFile])

  const effectivePreview = localPreview ?? initialImageUrl ?? null

  const watched = watch()
  const previewProps = useMemo(
    () => ({
      titulo: watched.titulo ?? '',
      descripcion: watched.descripcion ?? '',
      ctaTexto: watched.ctaTexto ?? '',
      fechaPublicacion: watched.fechaPublicacion ?? '',
      featured: Boolean(watched.featured),
      imageSrc: effectivePreview,
    }),
    [watched, effectivePreview],
  )

  async function onValid(values: NoticiaFormValues) {
    await onSubmit({ values, imageFile })
  }

  return (
    <div className="flex flex-col gap-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">{title}</h1>
        {description ? (
          <p className="text-sm text-muted-foreground">{description}</p>
        ) : null}
      </div>

      <form
        onSubmit={handleSubmit(onValid)}
        className="grid grid-cols-1 gap-8 lg:grid-cols-[minmax(0,1fr)_auto]"
      >
        <div className="flex flex-col gap-5">
          <Field
            label="Título"
            htmlFor="titulo"
            error={errors.titulo?.message}
            hint="Máximo 120 caracteres."
          >
            <Input
              id="titulo"
              maxLength={120}
              placeholder="Ej: Nueva cata de Tannat"
              {...register('titulo')}
            />
          </Field>

          <Field
            label="Descripción"
            htmlFor="descripcion"
            error={errors.descripcion?.message}
          >
            <Textarea
              id="descripcion"
              rows={5}
              placeholder="Contanos de qué se trata la noticia..."
              {...register('descripcion')}
            />
          </Field>

          <Field
            label="Texto del CTA"
            htmlFor="ctaTexto"
            error={errors.ctaTexto?.message}
          >
            <Input
              id="ctaTexto"
              placeholder="Ej: Reservar lugar"
              maxLength={60}
              {...register('ctaTexto')}
            />
          </Field>

          <Controller
            control={control}
            name="ctaUrl"
            render={({ field }) => (
              <CtaUrlField
                value={field.value ?? ''}
                onChange={field.onChange}
                error={errors.ctaUrl?.message}
              />
            )}
          />

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Field
              label="Fecha de publicación"
              htmlFor="fechaPublicacion"
              error={errors.fechaPublicacion?.message}
            >
              <Input
                id="fechaPublicacion"
                type="date"
                {...register('fechaPublicacion')}
              />
            </Field>

            <Field
              label="Estado"
              htmlFor="estado"
              error={errors.estado?.message}
              hint="Las noticias en borrador no se muestran en la app."
            >
              <Select id="estado" {...register('estado')}>
                <option value="BORRADOR">Borrador</option>
                <option value="PUBLICADA">Publicada</option>
              </Select>
            </Field>
          </div>

          <div className="flex flex-col gap-2">
            <Label>Destacada</Label>
            <Controller
              control={control}
              name="featured"
              render={({ field }) => (
                <div className="flex items-start gap-3 rounded-md border bg-muted/30 p-3">
                  <Switch
                    checked={field.value}
                    onChange={(e) => field.onChange(e.target.checked)}
                  />
                  <div className="text-xs text-muted-foreground">
                    Esta noticia se mostrara fullscreen a cada usuario una
                    unica vez cuando abra la app. Solo aplica si esta publicada.
                  </div>
                </div>
              )}
            />
          </div>

          <div className="flex flex-col gap-2">
            <Label>Imagen</Label>
            <ImagePicker
              previewUrl={effectivePreview}
              onSelect={(file) => {
                setImageError(null)
                setImageFile(file)
              }}
              onClear={() => {
                setImageError(null)
                setImageFile(null)
              }}
              onError={(msg) => setImageError(msg)}
            />
            {imageError ? (
              <p className="text-xs text-destructive">{imageError}</p>
            ) : null}
            <p className="text-xs text-muted-foreground">
              La imagen se sube despues de guardar la noticia.
            </p>
          </div>

          <div className="flex items-center justify-end gap-2 pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={onCancel}
              disabled={submitting}
            >
              Cancelar
            </Button>
            <Button type="submit" disabled={submitting}>
              {submitting ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : null}
              {submitLabel}
            </Button>
          </div>
        </div>

        <aside className="justify-self-center lg:sticky lg:top-6 lg:self-start">
          <MobilePreview {...previewProps} />
        </aside>
      </form>
    </div>
  )
}

interface FieldProps {
  label: string
  htmlFor: string
  error?: string
  hint?: string
  children: ReactNode
}

function Field({ label, htmlFor, error, hint, children }: FieldProps): ReactNode {
  return (
    <div className="flex flex-col gap-1.5">
      <Label htmlFor={htmlFor}>{label}</Label>
      {children}
      {error ? (
        <p className="text-xs text-destructive">{error}</p>
      ) : hint ? (
        <p className="text-xs text-muted-foreground">{hint}</p>
      ) : null}
    </div>
  )
}
